import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { CATEGORIES } from "@/data/site";
import { Reveal } from "@/components/Reveal";

export default function CategoriesGrid() {
  const featured = CATEGORIES.slice(0, 12);

  return (
    <section
      data-testid="categories-section"
      id="databases"
      className="relative py-24 md:py-32 border-t border-slate-100 bg-[#fafafa]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
          <div className="md:col-span-6">
            <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Databases</div>
            <h2 className="mt-4 font-display text-4xl md:text-6xl font-light tracking-tight text-slate-900 leading-[1.02]">
              A library of India,<br /> <span className="font-serif-editorial italic">segmented</span> for growth.
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8 flex md:items-end">
            <p className="text-base md:text-lg text-slate-600 leading-relaxed">
              Twenty-plus verticals — from GST-registered businesses and licensed doctors to F&O
              traders and RERA developers. Each dataset is scoped, filtered and delivered to fit
              your exact use case.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {featured.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.key}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: (i % 3) * 0.06 + Math.floor(i / 3) * 0.04, duration: 0.7 }}
              >
                <Link
                  to={c.key === "gstsp" ? "/gst-sales-purchase-data" : c.key === "newco" ? "/mca-company-database" : c.key === "gst" ? "/gst-database" : "/databases"}
                  data-testid={`category-card-${c.key}`}
                  className="group relative flex flex-col justify-between h-full bg-white border border-slate-100 rounded-3xl p-7 md:p-8 hover-lift hover:shadow-xl hover:shadow-slate-900/[0.05] hover:border-blue-100 overflow-hidden"
                >
                  <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-blue-50 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                  <div className="relative flex items-start justify-between">
                    <div className="w-11 h-11 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center transition-all duration-500 group-hover:bg-blue-600 group-hover:border-blue-600">
                      <Icon className="w-5 h-5 text-slate-700 transition-colors duration-500 group-hover:text-white" strokeWidth={1.5} />
                    </div>
                    <ArrowUpRight className="w-5 h-5 text-slate-300 transition-all duration-500 group-hover:text-slate-900 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.5} />
                  </div>
                  <div className="relative mt-16 md:mt-24">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-slate-400 font-medium">
                      /{String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="mt-2 font-display text-xl md:text-2xl font-medium text-slate-900 tracking-tight">
                      {c.name}
                    </div>
                    <div className="mt-3 text-sm text-slate-500 leading-relaxed">{c.desc}</div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        <Reveal className="mt-14 flex justify-center" delay={0.1}>
          <Link
            to="/databases"
            data-testid="view-all-databases"
            className="group inline-flex items-center gap-2 rounded-full bg-slate-900 text-white pl-6 pr-5 py-4 text-sm font-medium transition-all duration-300 hover:bg-blue-600 hover:shadow-lg hover:shadow-blue-500/20"
          >
            View All Databases
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.75} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}
